// Inventory: Duplicates (Starchive-Inventory-Section-Plan, section 3.4; the canvas's I5 board). Shared barcodes (a scan can
// ring the wrong piece), listings that may be the same piece twice, look-alike titles (separate pieces by Tim's rule, shown
// so the condition line gets checked, never merged), several sizes on one listing, quantity above 1. Archive, never delete.
import { useState } from 'react'
import { Screen, useMeta } from '../ReportsApp'
import { reportsAction, useReport } from '../api'
import { int, money } from '../format'
import { AsyncButton, Badge, Card, Note, Tile } from '../ui'
import { OwnerChip, PhotoImg, type FloorPiece } from './inventory-ui'
import { PieceDrawer } from './PieceDrawer'

type DupPiece = FloorPiece & { barcode: string }
interface Group { key: string; barcode: string; pieces: DupPiece[]; same_title: boolean }
interface Lookalike { key: string; title: string; brand: string; count: number; list_value: number; pieces: DupPiece[] }
interface DupsData {
  tiles: { one_tag: number; same_piece: number; multi_variant: number; qty_above_1: number; lookalikes: number }
  one_tag: Group[]; same_piece: Group[]; lookalikes: Lookalike[]; lookalikes_more: number
  multi_variant: { product_id: string; title: string; brand: string; image_url: string | null; sizes: string[]; shared_barcode: boolean; owner: string | null; list_price: number }[]
  qty: DupPiece[]
}

export function Dups() {
  const { meta } = useMeta()
  const { data: d, error, loading, refresh } = useReport<DupsData>('inv_dups')
  const [ownerId, setOwnerId] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const [msg, setMsg] = useState('')
  const signer = ownerId.length === 4 ? (meta?.owners ?? []).find((o) => o.employee_id === ownerId) : undefined

  const act = async (action: string, body: Record<string, unknown>) => {
    setMsg('')
    try { await reportsAction(action, { ...body, employee_id: ownerId }); refresh() } catch (e) { setMsg((e as Error).message); throw e }
  }
  const dismissBtn = (key: string, pieces: { product_id: string }[]) => signer
    ? <AsyncButton small run={() => act('dup_dismiss', { group_key: key, members: pieces.map((p) => p.product_id) }).catch(() => {})}>Not a problem</AsyncButton>
    : <span className="rp-muted" style={{ fontSize: 12 }}>Owner ID (top right) to decide</span>

  const pieceCol = (p: DupPiece, w = 150) => (
    <div key={p.variant_id} className="rp-dpiece" style={{ width: w }}>
      <span onClick={() => setOpen(p.product_id)} style={{ cursor: 'pointer' }}><PhotoImg url={p.image_url} title={p.title} width={320} /></span>
      <b onClick={() => setOpen(p.product_id)} style={{ cursor: 'pointer' }}>{p.title}</b>
      <span><OwnerChip owner={p.owner} consignorKey={p.consignor_key} /></span>
      <span className="rp-sub">{money(p.list_price, true)} · {int(p.days_on_floor)} days{p.size ? ` · Sz ${p.size}` : ''}{p.cost_source === 'typed' ? ` · ${money(p.shopify_cost, true)} typed cost` : ''}</span>
    </div>
  )
  const t = d?.tiles

  return (
    <Screen title="Duplicates" sub="Shared barcodes and listings that may be the same piece. Archive, never delete"
      right={<label className="rp-ownerbar"><span>Owner ID</span>
        <input inputMode="numeric" autoComplete="off" value={ownerId} onChange={(e) => setOwnerId(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="••••" aria-label="Owner ID" />
        {ownerId.length === 4 && <Badge tone={signer ? 'green' : 'red'}>{signer ? signer.name : 'Not an owner'}</Badge>}
      </label>} wide>
      {error && <div className="rp-error">{error}</div>}
      {msg && <div className="rp-error">{msg}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="One tag on different pieces" tone={t && t.one_tag ? 'red' : 'plain'} value={t ? int(t.one_tag) : '…'} sub="A scan of that tag can ring the wrong piece" />
        <Tile label="Same piece listed twice?" tone={t && t.same_piece ? 'amber' : 'plain'} value={t ? int(t.same_piece) : '…'} sub="Same barcode, same name, both on the floor" />
        <Tile label="Several sizes on one listing" value={t ? int(t.multi_variant) : '…'} sub="The listing spec is one piece per listing" />
        <Tile label="Quantity above 1" value={t ? int(t.qty_above_1) : '…'} sub="The counter treats one listing as one piece" />
        <Tile label="Look-alike names" value={t ? int(t.lookalikes) : '…'} sub="Separate pieces, told apart by size and condition. Checked, not merged" />
      </div>
      <div className="rp-grid rp-grid--2e" style={{ alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {(d?.one_tag ?? []).map((g) => (
            <Card key={g.key} tone="red" title={`One tag on ${int(g.pieces.length)} different pieces`}
              note={<>Barcode <b>{g.barcode}</b>. Scanning it at the counter can ring up any of these. The extra pieces need new tags: print them from the intake flow on the iPad, retag at the rack, and the row clears when the barcodes differ.</>}>
              <div className="rp-dgroup">{g.pieces.map((p) => pieceCol(p))}</div>
              <div className="rp-dactions">{dismissBtn(g.key, g.pieces)}</div>
            </Card>
          ))}
          {(d?.same_piece ?? []).map((g) => (
            <Card key={g.key} tone="amber" title="Same piece listed twice?"
              note={<>Barcode <b>{g.barcode}</b>, same name, both on the floor. One piece with two listings, or two pieces sharing a tag? If it is one piece, keep one listing and archive the other (a listing with a sale is kept).</>}>
              <div className="rp-dgroup">{g.pieces.map((p) => pieceCol(p))}</div>
              <div className="rp-dactions">
                {signer && g.pieces.map((p) => <AsyncButton key={p.variant_id} small run={() => act('dup_archive', { product_id: p.product_id, group_key: g.key }).catch(() => {})}>Archive the {p.days_on_floor}-day listing</AsyncButton>)}
                {dismissBtn(g.key, g.pieces)}
              </div>
            </Card>
          ))}
          <Card title="Look at these" tone="grey" note="Not duplicates, but the counter treats one listing as one piece">
            {(d?.multi_variant ?? []).map((m) => (
              <div key={m.product_id} className="rp-drow">
                <span className="rp-pcell" style={{ cursor: 'pointer' }} onClick={() => setOpen(m.product_id)}><PhotoImg url={m.image_url} title={m.title} width={96} small /><span className="rp-pcell__text"><b>{m.title}</b><span className="rp-sub">One listing, {m.sizes.length} sizes ({m.sizes.join(', ')})</span></span></span>
                {m.shared_barcode ? <Badge tone="amber">Same barcode on every size</Badge> : <Badge tone="grey">{int(m.sizes.length)} sizes</Badge>}
              </div>
            ))}
            {(d?.qty ?? []).map((p) => (
              <div key={p.variant_id} className="rp-drow">
                <span className="rp-pcell" style={{ cursor: 'pointer' }} onClick={() => setOpen(p.product_id)}><PhotoImg url={p.image_url} title={p.title} width={96} small /><span className="rp-pcell__text"><b>{p.title}</b><span className="rp-sub">Quantity {int(p.quantity)}</span></span></span>
                <Badge tone="grey">×{int(p.quantity)}</Badge>
              </div>
            ))}
            {!loading && (d?.multi_variant ?? []).length === 0 && (d?.qty ?? []).length === 0 && <Note>Nothing here</Note>}
          </Card>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card title="Look-alikes: separate pieces" tone="grey"
            note="Tim's rule: listings that look the same are separate pieces, possibly with different owners; size and condition tell them apart. Shown so the condition line gets checked, never merged.">
            {(d?.lookalikes ?? []).map((g) => (
              <div key={g.key} className="rp-lgroup">
                <b>{g.title} · {int(g.count)} listings, {money(g.list_value, true)} at list together</b>
                <div className="rp-lgroup__row">
                  {g.pieces.slice(0, 6).map((p) => (
                    <div key={p.variant_id} className="rp-lpiece" onClick={() => setOpen(p.product_id)}>
                      <PhotoImg url={p.image_url} title={p.title} width={220} />
                      {p.size && <b>Sz {p.size}</b>}
                      <OwnerChip owner={p.owner} consignorKey={p.consignor_key} />
                      <span className="rp-sub">{int(p.days_on_floor)} d</span>
                    </div>
                  ))}
                </div>
                <div className="rp-dactions">{dismissBtn(g.key, g.pieces)}</div>
              </div>
            ))}
            {(d?.lookalikes_more ?? 0) > 0 && <Note>And {int(d!.lookalikes_more)} more look-alike groups, smallest value last; dismiss these to see them.</Note>}
          </Card>
        </div>
      </div>
      {open && <PieceDrawer productId={open} onClose={() => setOpen(null)} onChanged={refresh} />}
    </Screen>
  )
}
